import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { Search, ChevronRight, UserCheck, ArrowDownLeft, ClipboardCheck, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';

const InboundQCList = ({ db, user }) => {
  const navigate = useNavigate();
  const [mics, setMics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Fetch MICs where status is pending_qc, qc_passed, or received
    const q = query(
      collection(db, "inwards"), 
      where("micStatus", "in", ["pending_qc", "qc_passed", "received"]),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const micList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setMics(micList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Inbound QC Queue: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredMics = useMemo(() => {
    return mics.filter(mic => 
      mic.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mic.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mic.micNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mic.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [mics, searchTerm]);

  const formatDate = (dateString) => {
      if (!dateString) return '---';
      try {
        const date = dateString.toDate ? dateString.toDate() : new Date(dateString);
        return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return '---';
      }
  };

  const getStatusBadge = (status) => {
    switch (status) {
        case 'pending_qc':
            return <div style={{fontSize: '11px', color: '#6366f1', fontWeight:'bold'}}>QC PENDING 🔍</div>;
        case 'qc_passed':
            return <div style={{fontSize: '11px', color: '#10b981', fontWeight:'bold'}}>QC PASSED ✅</div>;
        case 'received':
            return <div style={{fontSize: '11px', color: '#10b981', fontWeight:'bold'}}>RECEIVED ✅</div>;
        default:
            return <div style={{fontSize: '11px', color: '#64748b', fontWeight:'bold'}}>{status?.replace(/_/g, ' ')?.toUpperCase()}</div>;
    }
  };

  const handleRowClick = (mic) => {
    navigate(`/ops/inbound-qc/${mic.id}`);
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ClipboardCheck size={24} color="#2563eb" /> Quality Check / Gate Pass (Inward)
          </h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Verify incoming material quality and quantity at the gate.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={searchIconStyle} size={18} />
          <input 
            type="text" 
            placeholder="Search by Supplier/Customer, MIC No or Ref No..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
      </div>

      {mics.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <UserCheck size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No material waiting for Inbound QC!</h3>
          <p style={{ color: '#64748b' }}>Items appear here once the Material In Checklist is submitted.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={{...thStyle, textAlign:'left'}}>Supplier / Customer</th>
                <th style={thStyle}>Ref No. / Order No.</th>
                <th style={thStyle}>MIC Number</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredMics.map((mic) => (
                <tr 
                  key={mic.id} 
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => handleRowClick(mic)}
                  className="hover-highlight"
                >
                  <td style={tdStyle}>{formatDate(mic.updatedAt)}</td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{mic.supplierName || mic.customerName}</div>
                  </td>
                  <td style={tdStyle}>{mic.referenceNo || mic.orderNumber || '---'}</td>
                  <td style={tdStyle}>
                    <div style={{fontWeight: 'bold', fontSize: '13px'}}>{mic.micNo}</div>
                  </td>
                  <td style={tdStyle}>{mic.warehouse}</td>
                  <td style={tdStyle}>{getStatusBadge(mic.micStatus)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                      {mic.micStatus === 'pending_qc' ? 'Perform QC' : (mic.micStatus === 'qc_passed' ? 'Confirm Receipt' : 'View Details')} <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };

const filterBarStyle = { display: 'flex', gap: '15px', marginBottom: '20px' };
const searchInputStyle = { width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none' };
const searchIconStyle = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

export default InboundQCList;